import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Product, CartItem } from '../types';
import { calculateCartTotal, calculateItemPrice } from '../utils/pricingUtils';
import { supabase } from '../lib/supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import { trackAddToCart, trackBeginCheckout, shouldTrackAnalytics } from '../utils/siteAnalytics';
import { serializeCartItemsForTracking } from '../utils/cartSessionTracking';
import {
  isValidCartRecoverySessionId,
  mapRecoveredCartItems,
  type RecoverCartResponse,
} from '../utils/cartSessionRecovery';
import {
  clampProductQuantity,
} from '../utils/bookingFlowProducts';

interface CartContextType {
  items: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (productId: string) => void;
  clearCart: () => void;
  getTotalPrice: () => number;
  getCartCalculation: () => ReturnType<typeof calculateCartTotal>;
  sessionId: string;
  updateCartSession: (customerInfo?: { email: string; name: string }, reachedCheckout?: boolean) => Promise<void>;
  recoverCart: (sessionId: string) => Promise<boolean>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// Helper function to serialize dates for storage
const serializeCart = (items: CartItem[]) => {
  return JSON.stringify(items, (key, value) => {
    if (key === 'startDate' || key === 'endDate') {
      return value instanceof Date ? value.toISOString() : value;
    }
    return value;
  });
};

// Helper function to deserialize dates from storage
const deserializeCart = (cartJson: string | null): CartItem[] => {
  if (!cartJson) return [];
  
  try {
    const parsed = JSON.parse(cartJson, (key, value) => {
      if (key === 'startDate' || key === 'endDate') {
        return value ? new Date(value) : null;
      }
      return value;
    }) as CartItem[];
    return (Array.isArray(parsed) ? parsed : []).map((item) => ({
      ...item,
      quantity: clampProductQuantity(item.product ?? {}, Number(item.quantity) || 1),
    }));
  } catch (error) {
    console.error('Error parsing cart from localStorage:', error);
    return [];
  }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    // Initialize from localStorage on component mount
    return deserializeCart(localStorage.getItem('cart'));
  });
  
  const [sessionId, setSessionId] = useState<string>(() => {
    // Get existing session ID or create a new one
    const existingSessionId = localStorage.getItem('cartSessionId');
    return existingSessionId || uuidv4();
  });

  const getCartCalculation = useCallback(() => {
    return calculateCartTotal(items);
  }, [items]);

  // Helper to push "begin_checkout" event to GTM/GA4
  const pushBeginCheckout = useCallback(() => {
    try {
      if (typeof window === "undefined") return;
      if (!shouldTrackAnalytics()) return;

      const calculation = getCartCalculation();
      if (calculation.totalFinalPrice <= 0) return;

      const w = window as any;
      w.dataLayer = Array.isArray(w.dataLayer) ? w.dataLayer : [];

      const payload = {
        event: "begin_checkout",
        currency: "SEK",
        value: Number(calculation.totalFinalPrice) || 0,
        items: items.map((item) => ({
          item_id: item.product.id,
          item_name: item.product.name,
          item_category: item.product.category,
          quantity: item.quantity,
        })),
      };

      console.log("[tracking] pushing begin_checkout", payload);
      w.dataLayer.push(payload);
      console.log("[tracking] dataLayer last", w.dataLayer.slice(-1));
      trackBeginCheckout(window.location.pathname);
    } catch (e) {
      console.warn("[tracking] begin_checkout push failed", e);
    }
  }, [items, getCartCalculation]);

  const persistCartSession = useCallback(async (
    customerInfo?: { email: string; name: string },
    reachedCheckout: boolean = false,
  ) => {
    const calculation = getCartCalculation();
    const slimItems = items.length > 0 ? serializeCartItemsForTracking(items) : [];
    const totalAmount =
      items.length > 0 ? Math.round(Number(calculation.totalFinalPrice)) || 0 : 0;

    const syncViaRpc = async (activeSessionId: string) => {
      const rpcArgs = {
        p_session_id: activeSessionId,
        p_items: slimItems,
        p_total_amount: totalAmount,
        p_customer_email: customerInfo?.email || null,
        p_customer_name: customerInfo?.name || null,
        p_reached_checkout: reachedCheckout ? true : null,
      };

      const tryRpc = async (fn: string) => {
        const { data, error } = await supabase.rpc(fn, rpcArgs);
        return { data, error };
      };

      let result = await tryRpc('sync_storefront_basket');
      if (
        result.error &&
        /sync_storefront_basket|Could not find the function|PGRST202/i.test(
          result.error.message || ''
        )
      ) {
        result = await tryRpc('upsert_cart_session');
      }

      return { result };
    };

    let activeSessionId = sessionId;
    let rpcError: { message?: string } | null = null;

    try {
      let { result } = await syncViaRpc(activeSessionId);
      rpcError = result.error;

      // Null on non-empty basket = existing checkout_completed row blocks upsert.
      if (!result.error && slimItems.length > 0 && !result.data) {
        const rotatedSessionId = uuidv4();
        activeSessionId = rotatedSessionId;
        setSessionId(rotatedSessionId);
        localStorage.setItem('cartSessionId', rotatedSessionId);

        const retry = await syncViaRpc(activeSessionId);
        result = retry.result;
        rpcError = result.error;
        if (!result.error && slimItems.length > 0 && !result.data) {
          rpcError = { message: 'RPC returned null for non-empty basket after session rotation' };
        }
      }

      if (!rpcError) return;
    } catch (err) {
      rpcError = {
        message: err instanceof Error ? err.message : 'RPC threw',
      };
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !supabaseAnonKey) {
      console.warn('[Cart] basket sync failed and Supabase env missing:', rpcError?.message);
      return;
    }

    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/abandoned-cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseAnonKey}`,
          apikey: supabaseAnonKey,
        },
        body: JSON.stringify({
          action: 'track',
          sessionId: activeSessionId,
          customerEmail: customerInfo?.email || null,
          customerName: customerInfo?.name || null,
          items: slimItems,
          totalAmount,
          ...(reachedCheckout ? { reachedCheckout: true } : {}),
        }),
      });
      if (!res.ok) {
        console.warn('[Cart] basket sync failed:', rpcError?.message, res.status);
      }
    } catch (err) {
      console.warn(
        '[Cart] basket sync failed (RPC + edge):',
        rpcError?.message,
        err instanceof Error ? err.message : err
      );
    }
  }, [items, sessionId, getCartCalculation]);

  const updateCartSession = useCallback(async (
    customerInfo?: { email: string; name: string },
    reachedCheckout: boolean = false,
  ) => {
    if (reachedCheckout) {
      pushBeginCheckout();
    }

    try {
      await persistCartSession(customerInfo, reachedCheckout);
    } catch (err) {
      console.warn('[Cart] Varukorgsspårning misslyckades:', err);
    }
  }, [persistCartSession, pushBeginCheckout]);

  const markCartCompleted = useCallback(async () => {
    // Validate environment variables
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('Missing Supabase environment variables for markCartCompleted');
      return;
    }
    
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/abandoned-cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          action: 'complete',
          sessionId
        })
      });
      if (!res.ok) console.warn('[Cart] abandoned-cart complete failed:', res.status);
    } catch (_err) {
      if (import.meta.env.DEV) console.warn('[Cart] Kunde inte markera varukorg som slutförd (abandoned-cart).');
    }
  }, [sessionId]);

  const recoverCart = useCallback(async (recoverySessionId: string): Promise<boolean> => {
    if (!isValidCartRecoverySessionId(recoverySessionId)) {
      return false;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('Missing Supabase environment variables for recoverCart');
      return false;
    }

    try {
      // Service-role path returns cart items — avoids anon RLS on identified carts
      const res = await fetch(`${supabaseUrl}/functions/v1/abandoned-cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseAnonKey}`,
          apikey: supabaseAnonKey,
        },
        body: JSON.stringify({
          action: 'recover',
          sessionId: recoverySessionId,
        }),
      });
      if (!res.ok) return false;

      const payload = (await res.json()) as RecoverCartResponse;
      const recoveredItems = mapRecoveredCartItems(payload.items);
      if (!payload.success || recoveredItems.length === 0) {
        return false;
      }

      setItems(recoveredItems);
      setSessionId(payload.sessionId || recoverySessionId);
      localStorage.setItem('cartSessionId', payload.sessionId || recoverySessionId);

      return true;
    } catch (_err) {
      if (import.meta.env.DEV) console.warn('[Cart] Kunde inte återställa varukorg (abandoned-cart).');
      return false;
    }
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem('cart');

    // Mark cart as completed when clearing after successful checkout
    if (items.length > 0) {
      markCartCompleted();
    }

    // Generate a new session ID for future carts
    const newSessionId = uuidv4();
    setSessionId(newSessionId);
    localStorage.setItem('cartSessionId', newSessionId);
  }, [items, markCartCompleted]);

  // Save to localStorage whenever items change; sync including empty carts
  const prevItemsLenRef = useRef<number | null>(null);
  useEffect(() => {
    localStorage.setItem('cart', serializeCart(items));
    localStorage.setItem('cartSessionId', sessionId);

    const prevLen = prevItemsLenRef.current;
    prevItemsLenRef.current = items.length;

    // Skip initial mount when already empty (no DB row to clear)
    if (items.length === 0 && (prevLen === null || prevLen === 0)) {
      return;
    }

    updateCartSession();
  }, [items, sessionId, updateCartSession]);

  // Track cart abandonment
  useEffect(() => {
    // Mark cart as completed when user leaves the site
    const handleBeforeUnload = () => {
      if (items.length > 0 && window.location.pathname.includes('/checkout')) {
        // If user is in checkout flow, track this as a potential abandonment
        // The edge function will determine if it's truly abandoned after a time threshold
        updateCartSession();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [items, updateCartSession]);

  // Check for cart recovery from URL
  useEffect(() => {
    const checkForRecovery = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const recoveryId = urlParams.get('recover');

      if (recoveryId) {
        // Try to recover cart
        const recovered = await recoverCart(recoveryId);

        if (recovered) {
          // Remove recovery param from URL without page reload
          const newUrl = window.location.pathname +
            window.location.search.replace(/[?&]recover=[^&]+/, '') +
            window.location.hash;
          window.history.replaceState({}, document.title, newUrl);
        }
      }
    };

    checkForRecovery();
  }, [recoverCart]);

  const addToCart = (item: CartItem) => {
    const quantity = clampProductQuantity(item.product, item.quantity);
    const normalizedItem: CartItem = { ...item, quantity };

      // --- Tracking (GTM / GA4) ---
  try {
    if (typeof window !== "undefined" && shouldTrackAnalytics()) {
      const priceCalc = calculateItemPrice(
        normalizedItem.product.pricePerDay,
        normalizedItem.quantity,
        normalizedItem.startDate,
        normalizedItem.endDate,
        normalizedItem.product.upgrades,
        normalizedItem.options,
        normalizedItem.addons,
        { category: normalizedItem.product.category, slug: normalizedItem.product.slug },
        normalizedItem.product.optionalComponents,
        normalizedItem.removedComponents
      );

  const w = window as any;
w.dataLayer = Array.isArray(w.dataLayer) ? w.dataLayer : [];

const payload = {
  event: "add_to_cart",
  item_name: normalizedItem.product.name,
  value: Number(priceCalc.finalPrice) || 0,
  currency: "SEK",
  quantity: normalizedItem.quantity,
  item_id: normalizedItem.product.id,
  item_category: normalizedItem.product.category,
  days: priceCalc.days,
};

console.log("[tracking] pushing add_to_cart", payload);
w.dataLayer.push(payload);
console.log("[tracking] dataLayer last", w.dataLayer.slice(-1));
trackAddToCart(normalizedItem.product);
    }
  } catch (e) {
    console.warn("[tracking] add_to_cart push failed", e);
  }
    setItems((prevItems) => {
      // Check if item already exists
      const existingItemIndex = prevItems.findIndex(
        (i) => i.product.id === normalizedItem.product.id
      );

      if (existingItemIndex >= 0) {
        // Update existing item
        const updatedItems = [...prevItems];
        const mergedQuantity = clampProductQuantity(
          normalizedItem.product,
          prevItems[existingItemIndex].quantity + normalizedItem.quantity
        );
        updatedItems[existingItemIndex] = {
          ...prevItems[existingItemIndex],
          quantity: mergedQuantity,
          startDate: normalizedItem.startDate,
          endDate: normalizedItem.endDate,
          options: normalizedItem.options,
          removedComponents: normalizedItem.removedComponents,
          addons: normalizedItem.addons,
        };
        return updatedItems;
      } else {
        // Add new item
        return [...prevItems, normalizedItem];
      }
    });
  };

  const removeFromCart = (productId: string) => {
    setItems((prevItems) =>
      prevItems.filter((item) => item.product.id !== productId)
    );
  };

  const getTotalPrice = useCallback(() => {
    const calculation = getCartCalculation();
    return calculation.totalFinalPrice;
  }, [getCartCalculation]);

  return (
    <CartContext.Provider
      value={{ 
        items, 
        addToCart, 
        removeFromCart, 
        clearCart, 
        getTotalPrice, 
        getCartCalculation,
        sessionId,
        updateCartSession,
        recoverCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};